import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Desktop menus open from their text label; surfaces follow disclosure state. */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];
  #activeItem = null;
  #trigger = null;
  #listeners = null;
  #submenuObserver = null;
  #closeTimer = null;

  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    this.addEventListener('click', this.#handleClick, options);
    this.addEventListener('keydown', this.#handleKeydown, options);
    this.addEventListener('focusout', this.#handleFocusOut, options);
    this.headerComponent?.addEventListener('pointerenter', this.#cancelClose, options);
    this.headerComponent?.addEventListener('pointerleave', this.#scheduleClose, options);
    document.addEventListener('pointerdown', this.#handleOutsidePointer, options);
    window.addEventListener('resize', this.#handleResize, options);
    window.addEventListener('pagehide', this.#close, options);
    this.querySelectorAll('[ref="menuitem"][aria-haspopup], [slot="more"] > button').forEach((trigger) => {
      trigger.setAttribute('role', 'button');
      trigger.setAttribute('aria-expanded', 'false');
    });
    this.querySelectorAll('.menu-list__link-title').forEach((label) => {
      label.addEventListener('pointerenter', this.#handleLabelEnter, options);
      // Keep the menu open while crossing the header toward its links.
    });
    this.querySelectorAll('[ref="submenu[]"]').forEach((submenu) => {
      submenu.addEventListener('pointerenter', this.#cancelClose, options);
      submenu.addEventListener('pointerleave', this.#scheduleClose, options);
    });
    const search = document.getElementById('search-modal');
    search?.addEventListener('dialog:open', this.#handleSearchOpen, options);
    search?.addEventListener('dialog:close', this.#syncSurface, options);
    this.#close();
    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    this.#close();
    this.#listeners?.abort();
    super.disconnectedCallback();
  }

  // Ignore legacy handlers attached to the entire list item. Only the visible
  // text label is a pointer trigger; keyboard and touch retain disclosure.
  activate() {}
  deactivate() {}

  get headerComponent() {
    return this.closest('header-component');
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]');
  }

  #getTrigger(target) {
    if (!(target instanceof Element)) return null;
    const trigger = target.closest('[ref="menuitem"], [slot="more"] > button');
    return trigger && this.contains(trigger) ? trigger : null;
  }

  #getItem(trigger) {
    if (trigger.parentElement?.slot === 'more') {
      return this.querySelector('[slot="overflow"] [ref="menuitem"]');
    }
    return trigger;
  }

  #handleLabelEnter = (event) => {
    if (event.pointerType === 'touch' || !matchMedia('(hover: hover)').matches) return;
    const trigger = this.#getTrigger(event.currentTarget);
    if (!trigger) return;
    const item = this.#getItem(trigger);
    if (!item || (!findSubmenu(item) && trigger.parentElement?.slot !== 'more')) {
      this.#close();
      return;
    }
    this.#cancelClose();
    this.#open(item, trigger);
  };

  #cancelClose = () => {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = null;
  };

  #scheduleClose = (event) => {
    if (event.pointerType === 'touch') return;
    this.#cancelClose();
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    this.#closeTimer = setTimeout(() => {
      if (this.headerComponent?.matches(':hover')) return;
      this.#close();
    }, 450);
  };

  #handleSearchOpen = () => {
    this.#close();
    this.#syncSurface();
  };

  #syncSurface = () => {
    const header = this.headerComponent;
    if (!header) return;
    const open = Boolean(header.querySelector('header-menu[aria-expanded="true"]') || document.querySelector('#search-modal dialog[open]'));
    header.toggleAttribute('data-disclosure-open', open);
  };

  #handleClick = (event) => {
    const trigger = this.#getTrigger(event.target);
    if (!trigger || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const item = this.#getItem(trigger);
    if (!item || (!findSubmenu(item) && trigger.parentElement?.slot !== 'more')) return;
    if (event.detail > 0 && event.pointerType !== 'touch' && matchMedia('(hover: hover)').matches) {
      event.preventDefault();
      this.#open(item, trigger);
      return;
    }
    event.preventDefault();
    if (this.#activeItem === item && this.#trigger === trigger) {
      this.#close();
    } else {
      this.#open(item, trigger);
    }
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.#activeItem) {
      event.preventDefault();
      event.stopPropagation();
      const trigger = this.#trigger;
      this.#close();
      trigger?.focus({ preventScroll: true });
      return;
    }
    const trigger = this.#getTrigger(event.target);
    if (!trigger) return;
    const item = this.#getItem(trigger);
    if (!item || (!findSubmenu(item) && trigger.parentElement?.slot !== 'more')) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.#open(item, trigger);
      findSubmenu(item)?.querySelector('a[href], button')?.focus({ preventScroll: true });
    } else if (event.key === ' ' && trigger.tagName === 'A') {
      event.preventDefault();
      if (this.#activeItem === item) this.#close();
      else this.#open(item, trigger);
    }
    // Enter on links and Enter/Space on native buttons produce a real click.
  };

  #handleOutsidePointer = (event) => {
    if (!event.composedPath().includes(this)) this.#close();
  };

  #handleFocusOut = (event) => {
    // relatedTarget already identifies the destination while activeElement can
    // briefly be body between blur and focus, before an interior link clicks.
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (this.headerComponent?.matches(':hover')) return;
    this.#close();
  };

  #handleResize = debounce(() => {
    this.#close();
    setHeaderMenuStyle();
  }, 100);

  #open(item, trigger) {
    if (!this.headerComponent || !this.isConnected) return;
    this.#cancelClose();
    if (this.#activeItem === item && this.#trigger === trigger) return;
    this.#close();
    this.#activeItem = item;
    this.#trigger = trigger;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    trigger.ariaExpanded = 'true';
    this.dataset.overflowExpanded = String(item.parentElement?.slot === 'overflow');
    const submenu = findSubmenu(item);
    if (submenu) submenu.dataset.active = '';
    this.#measure();
    this.style.setProperty('--submenu-opacity', '1');
    this.#syncSurface();
    this.dispatchEvent(new MegaMenuHoverEvent());
    // Keep height in sync with deferred images/content. Closing or switching
    // disconnects this observer, so old measurements cannot reopen a menu.
    if (submenu) {
      this.#submenuObserver = new ResizeObserver(() => {
        if (this.#activeItem === item) this.#measure();
      });
      this.#submenuObserver.observe(submenu);
    }
  }

  #measure() {
    const item = this.#activeItem;
    if (!item) return;
    const submenu = findSubmenu(item);
    let height = submenu?.offsetHeight || 0;
    if (item.parentElement?.slot === 'overflow') {
      height = Math.max(height, this.overflowMenu?.offsetHeight || 0);
    }
    this.headerComponent?.style.setProperty('--submenu-height', `${height}px`);
    this.#setFullOpenHeaderHeight(height);
  }

  #close = () => {
    this.#cancelClose();
    this.#submenuObserver?.disconnect();
    this.#submenuObserver = null;
    const submenu = findSubmenu(this.#activeItem);
    if (submenu) delete submenu.dataset.active;
    if (this.#activeItem) this.#activeItem.ariaExpanded = 'false';
    if (this.#trigger) this.#trigger.ariaExpanded = 'false';
    this.#activeItem = null;
    this.#trigger = null;
    this.ariaExpanded = 'false';
    this.dataset.overflowExpanded = 'false';
    this.style.setProperty('--submenu-opacity', '0');
    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0);
    this.#syncSurface();
  };

  #setFullOpenHeaderHeight(submenuHeight) {
    const header = this.headerComponent;
    if (!header) return;
    const visibleHeight = header.hasAttribute('data-submenu-overlap-bottom-row')
      ? header.querySelector('.header__row--top')?.offsetHeight || 0
      : header.offsetHeight;
    header.style.setProperty('--full-open-header-height', `${submenuHeight ? submenuHeight + visibleHeight : 0}px`);
  }

  #preloadImages = () => {
    if (!this.isConnected) return;
    this.querySelectorAll('img[loading="lazy"]').forEach((image) => image.removeAttribute('loading'));
  };
}

function findSubmenu(item) {
  return item?.parentElement?.querySelector('[ref="submenu[]"]') || null;
}

if (!customElements.get('header-menu')) customElements.define('header-menu', HeaderMenu);
